/**
 * The editor's Options tab: the customer's design options side by side (panels, kW, kWh, price, savings, payback and
 * monthly payment), a new option copied from the design being edited, renaming, and the chosen option (the one the
 * website quote and proposal use). Each option is its own design; "Open" switches the editor to it.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Pencil } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorMessage, unwrap, type S } from "../api/client";
import { Alert, Badge, Button, Spinner, TextInput, useToast } from "../components/ui";
import { useT } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/keys";
import { money, num } from "../lib/format";
import { PAYMENT_LABEL_KEY } from "./payments.ts";
import "./plan.css";

export function useDesignOptions(designId: string) {
  return useQuery({
    queryKey: ["design-options", designId],
    queryFn: () => unwrap(api.GET("/v1/designs/{design_id}/options", { params: { path: { design_id: designId } } })),
  });
}

export function OptionsPanel({ designId, canEdit, dirty }: { designId: string; canEdit: boolean; dirty: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const t = useT();
  const options = useDesignOptions(designId);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const refresh = (data?: S["DesignOptionList"]) => {
    if (data) for (const o of data.items) qc.setQueryData(["design-options", o.design_id], data);
    qc.invalidateQueries({ queryKey: ["design-options"] });
  };
  const create = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/designs/{design_id}/options", { params: { path: { design_id: designId } }, body: {} })),
    onSuccess: (o) => {
      refresh();
      toast(t("editor.options.created", { option: o.label }));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const rename = useMutation({
    mutationFn: ({ id, label }: { id: string; label: string }) => unwrap(api.PATCH("/v1/designs/{design_id}/option", { params: { path: { design_id: id } }, body: { label } })),
    onSuccess: () => {
      setRenaming(null);
      refresh();
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const choose = useMutation({
    mutationFn: (id: string) => unwrap(api.POST("/v1/designs/{design_id}/option/choose", { params: { path: { design_id: id } } })),
    onSuccess: (data, id) => {
      refresh(data);
      const chosen = data.items.find((o) => o.design_id === id)?.label;
      toast(chosen ? t("editor.options.chosenToast", { option: chosen }) : t("editor.options.chosenToastUnnamed"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });

  if (options.isLoading) return <Spinner label={t("editor.loading")} />;
  if (options.error || !options.data) return <Alert tone="danger" title={t("editor.options.loadError")}>{errorMessage(options.error)}</Alert>;
  const items = options.data.items;
  const currency = items.find((o) => o.numbers?.currency)?.numbers?.currency ?? "USD";
  const rows: [MessageKey, (n: S["DesignOptionNumbers"]) => string][] = [
    ["editor.options.row.panels", (n) => num(n.panel_count)],
    ["editor.options.row.system", (n) => `${num(n.kw_dc, 2)} kW`],
    ["editor.options.row.production", (n) => (n.annual_kwh != null ? `${num(n.annual_kwh)} kWh` : "—")],
    ["editor.options.row.price", (n) => money(n.price, n.currency ?? currency)],
    ["editor.options.row.year1Savings", (n) => money(n.year1_savings, n.currency ?? currency)],
    ["editor.options.row.payback", (n) => (n.payback_years != null ? t("editor.options.paybackYears", { years: Math.round(n.payback_years * 10) / 10 }) : "—")],
    ["editor.options.row.monthlyPayment", (n) => (n.monthly_payment != null ? `${money(n.monthly_payment, n.currency ?? currency)}${n.payment_type ? ` · ${t(PAYMENT_LABEL_KEY[n.payment_type])}` : ""}` : "—")],
  ];

  return (
    <section className="stack plan-panel">
      <p className="muted small">{t("editor.options.intro")}</p>
      {canEdit && (
        <Button icon={<Copy size={16} />} loading={create.isPending} onClick={() => create.mutate()} title={dirty ? t("editor.options.copyFromSaved") : undefined}>
          {t("editor.options.new")}
        </Button>
      )}
      {dirty && canEdit && <p className="small warn-text">{t("editor.options.unsavedNotCopied")}</p>}
      <div className="options-scroll">
      <div className="options-compare" style={{ gridTemplateColumns: `auto repeat(${items.length}, minmax(104px, 1fr))` }}>
        <div className="oc-head" />
        {items.map((o) => (
          <div key={o.design_id} className={`oc-head${o.design_id === designId ? " current" : ""}${o.chosen ? " chosen" : ""}`}>
            {renaming === o.design_id ? (
              <form
                className="row"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (label.trim()) rename.mutate({ id: o.design_id, label: label.trim() });
                }}
              >
                <TextInput value={label} maxLength={60} autoFocus onChange={(e) => setLabel(e.target.value)} aria-label={t("editor.options.name")} />
                <Button size="sm" variant="primary" type="submit" loading={rename.isPending} icon={<Check size={14} />} aria-label={t("editor.options.saveName")} />
              </form>
            ) : (
              <b className="row">
                {o.label}
                {canEdit && (
                  <button
                    className="icon-btn tiny"
                    aria-label={t("editor.options.rename", { option: o.label })}
                    onClick={() => {
                      setRenaming(o.design_id);
                      setLabel(o.label);
                    }}
                  >
                    <Pencil size={13} />
                  </button>
                )}
              </b>
            )}
            <span className="small muted">
              {o.design_id === designId ? t("editor.options.openNow") : o.version_number != null ? t("editor.options.version", { version: o.version_number }) : "v–"}
              {o.chosen && (
                <>
                  {" "}
                  <Badge tone="ok">{t("editor.options.chosen")}</Badge>
                </>
              )}
            </span>
          </div>
        ))}
        {rows.map(([name, value]) => (
          <Row key={name} name={t(name)} items={items} value={value} designId={designId} />
        ))}
        <div className="oc-name" />
        {items.map((o) => (
          <div key={o.design_id} className={`oc-cell actions${o.design_id === designId ? " current" : ""}${o.chosen ? " chosen" : ""}`}>
            {o.design_id !== designId && (
              <Button size="sm" onClick={() => navigate(`/designs/${o.design_id}/edit`)} disabled={dirty} title={dirty ? t("editor.options.saveFirst") : undefined}>
                {t("editor.options.open")}
              </Button>
            )}
            {canEdit && !o.chosen && (
              <Button size="sm" variant="primary" loading={choose.isPending && choose.variables === o.design_id} disabled={!o.numbers} onClick={() => choose.mutate(o.design_id)}>
                {t("editor.options.choose")}
              </Button>
            )}
          </div>
        ))}
      </div>
      </div>
      {items.some((o) => o.note) && (
        <p className="small muted">
          {items
            .filter((o) => o.note)
            .map((o) => t("editor.options.note", { option: o.label, note: o.note ?? "" }))
            .join(" · ")}
        </p>
      )}
      {!options.data.chosen_design_id && items.length > 1 && <p className="small muted">{t("editor.options.noneChosen")}</p>}
    </section>
  );
}

function Row({ name, items, value, designId }: { name: string; items: S["DesignOption"][]; value: (n: S["DesignOptionNumbers"]) => string; designId: string }) {
  return (
    <>
      <div className="oc-name small muted">{name}</div>
      {items.map((o) => (
        <div key={o.design_id} className={`oc-cell${o.design_id === designId ? " current" : ""}${o.chosen ? " chosen" : ""}`}>
          {o.numbers ? value(o.numbers) : "—"}
        </div>
      ))}
    </>
  );
}

